import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { authClient } from '../auth-client.ts';
import { ErrorText } from '../components/ErrorText.tsx';
import { PasswordFields } from '../components/PasswordFields.tsx';
import { useToast } from '../components/Toast.tsx';
import { setLang } from '../i18n/index.ts';
import type { Me } from '../types.ts';

const REMINDERS = [null, 15, 30, 60, 120, 1440] as const;

export function ProfilePage({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [first, setFirst] = useState(me.firstName);
  const [last, setLast] = useState(me.lastName);
  const [language, setLanguage] = useState(me.language);
  const [reminder, setReminder] = useState<number | null>(me.reminderMinutes);
  const [error, setError] = useState<unknown>(null);
  const [calendarUrl, setCalendarUrl] = useState(me.calendarUrl);
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState(['', '']);
  const [pwError, setPwError] = useState<string | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.patch('/api/me', {
        firstName: first,
        lastName: last,
        language,
        reminderMinutes: reminder,
      });
      setLang(language);
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast(t('common.saved'));
    } catch (err) {
      setError(err);
    }
  };
  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (pw[0] !== pw[1]) return setPwError(t('password.mismatch'));
    const r = await authClient.changePassword({
      currentPassword: current,
      newPassword: pw[0]!,
      revokeOtherSessions: true,
    });
    if (r.error)
      return setPwError(r.error.status === 400 ? t('errors.weak_password') : t('login.failed'));
    setCurrent('');
    setPw(['', '']);
    toast(t('profile.passwordChanged'));
  };
  const reminderLabel = (m: number | null) =>
    m === null
      ? t('profile.reminderOff')
      : m >= 60
        ? t('profile.hours', { count: m / 60 })
        : t('profile.minutes', { count: m });

  return (
    <div className="stack">
      <form className="card" onSubmit={save}>
        <h2>{t('profile.title')}</h2>
        <p className="k">
          {me.email}
          {me.companyName ? ` · ${me.companyName}` : ''}
        </p>
        <div className="row wrap">
          <label>
            {t('profile.firstName')}
            <input
              required
              maxLength={80}
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              autoComplete="given-name"
            />
          </label>
          <label>
            {t('profile.lastName')}
            <input
              required
              maxLength={80}
              value={last}
              onChange={(e) => setLast(e.target.value)}
              autoComplete="family-name"
            />
          </label>
        </div>
        <div className="row wrap">
          <label>
            {t('profile.language')}
            <select
              aria-label={t('profile.language')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'sv' | 'en')}
            >
              <option value="sv">Svenska</option>
              <option value="en">English</option>
            </select>
          </label>
          <label>
            {t('profile.reminder')}
            <select
              aria-label={t('profile.reminder')}
              value={String(reminder)}
              onChange={(e) =>
                setReminder(e.target.value === 'null' ? null : Number(e.target.value))
              }
            >
              {REMINDERS.map((m) => (
                <option key={String(m)} value={String(m)}>
                  {reminderLabel(m)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <ErrorText error={error} />
        <button className="btn">{t('common.save')}</button>
      </form>

      <section className="card">
        <h2>{t('profile.calendar')}</h2>
        <p className="k">{t('profile.calendarIntro')}</p>
        <div className="row">
          <input
            readOnly
            value={calendarUrl}
            aria-label={t('profile.calendar')}
            onFocus={(e) => e.target.select()}
          />
        </div>
        <div className="actions" style={{ marginTop: 8 }}>
          <button
            className="btn alt sm"
            onClick={async () => {
              await navigator.clipboard.writeText(calendarUrl).catch(() => {});
              toast(t('common.copied'));
            }}
          >
            {t('common.copy')}
          </button>
          <button
            className="btn alt sm"
            onClick={async () => {
              if (!window.confirm(t('profile.calendarNewConfirm'))) return;
              const r = await api.post<{ calendarUrl: string }>('/api/me/calendar-token');
              setCalendarUrl(r.calendarUrl);
            }}
          >
            {t('profile.calendarNew')}
          </button>
        </div>
      </section>

      <form className="card" onSubmit={changePassword}>
        <h2>{t('profile.changePassword')}</h2>
        <input type="email" autoComplete="username" value={me.email} readOnly hidden />
        <label>
          {t('profile.currentPassword')}
          <input
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <PasswordFields password={pw[0]!} repeat={pw[1]!} onChange={(a, b) => setPw([a, b])} />
        {pwError && (
          <p className="err" role="alert">
            {pwError}
          </p>
        )}
        <button className="btn">{t('profile.changePassword')}</button>
      </form>

      <section className="card">
        <h2>{t('profile.export')}</h2>
        <p className="k">{t('profile.exportIntro')}</p>
        <a className="btn alt" href="/api/me/export.csv" download>
          {t('profile.exportButton')}
        </a>
        <p className="k">{t('profile.deleteInfo')}</p>
      </section>
    </div>
  );
}
